import { useEffect, useState } from 'react'
import { Layer, Feature } from 'react-mapbox-gl'
import { MapPopupWrapper, Arrow, PopupLink } from './LocationMap.styled'
import { Popup } from 'react-mapbox-gl'
import {
  GymLocation,
  GlobalMapProps,
  StatesAU,
  LAYOUT_TEMPLATE
} from '../../services/api/types'
import { PAGE_TEMPLATE } from '../../services/api/constants'

function changeCursor(event: any, type: string) {
  const { map } = event.target.style
  map.getCanvas().style.cursor = type || 'grab'
}

export interface GymsMapLayerProps extends GlobalMapProps {
  onLocationClick?: Function
  locationMarker: HTMLElement
  state?: string
  states?: string[]
  template?: LAYOUT_TEMPLATE
}

type defaultStateObject = {
  [S in StatesAU]?: GymLocation[]
}

const defaultStates: defaultStateObject = {
  NSW: [],
  QLD: [],
  SA: [],
  VIC: [],
  WA: [],
  NT: [],
  TAS: [],
  other: []
}

const RenderLayers = (props: GymsMapLayerProps) => {
  const {
    zoomTo,
    mapData,
    setCenter,
    displaySub,
    locationMarker,
    state,
    template
  } = props
  const [location, setLocation] = useState(displaySub)

  return (
    <>
      <Layer
        type='symbol'
        id={`gym_pins_${state}`}
        images={['gymMarker', locationMarker]}
        layout={{ 'icon-image': 'gymMarker', 'icon-anchor': 'bottom' }}
        style={{ cursor: 'pointer' }}
        filter={['!has', 'point_count']}
        geoJSONSourceOptions={{
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50
        }}
        onMouseEnter={(e: any) => changeCursor(e, 'pointer')}
        onMouseLeave={(e: any) => changeCursor(e, 'grab')}
      >
        {mapData &&
          mapData.map((pin: GymLocation, idx: number) => (
            <Feature
              key={state ? state + idx : idx}
              properties={{ pin: pin }}
              coordinates={pin.geometry?.coordinates || []}
              onClick={() => {
                setCenter && setCenter(pin.geometry?.coordinates)
                setLocation(pin === location ? undefined : pin)
              }}
            />
          ))}
      </Layer>

      {/* clustered pin circles */}
      <Layer
        id={`gym_cluster_layer_${state}`}
        sourceId={`gym_pins_${state}`}
        filter={['has', 'point_count']}
        paint={{
          'circle-color':
            template === PAGE_TEMPLATE.YOU_FITNESS ? '#1B1B1B' : '#164cab',
          'circle-radius': {
            property: 'point_count',
            type: 'interval',
            stops: [
              [0, 1],
              [1, 1],
              [2, 20],
              [4, 30],
              [10, 40]
            ]
          },
          'circle-stroke-width': template === PAGE_TEMPLATE.YOU_FITNESS ? 2 : 3,
          'circle-stroke-color':
            template === PAGE_TEMPLATE.YOU_FITNESS ? '#2533D5' : '#ee6c16',
          'circle-stroke-opacity': 1
        }}
        type='circle'
        style={{ cursor: 'pointer' }}
        onClick={(event: any) => zoomTo && zoomTo(event)}
        onMouseEnter={(e: any) => changeCursor(e, 'pointer')}
        onMouseLeave={(e: any) => changeCursor(e, 'grab')}
      />

      {/* clustered pin labels */}
      <Layer
        id={`gym_cluster_count_${state}`}
        type='symbol'
        sourceId={`gym_pins_${state}`}
        filter={['has', 'point_count']}
        layout={{
          'text-field': '{point_count}',
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 16
        }}
        paint={{
          'text-color': 'white'
        }}
        onMouseEnter={(e: any) => changeCursor(e, 'pointer')}
        onMouseLeave={(e: any) => changeCursor(e, 'grab')}
      />
      {location && (
        <Popup
          style={{ top: '-2rem' }}
          coordinates={location.geometry?.coordinates || []}
        >
          <MapPopupWrapper template={template}>
            <PopupLink target='_blank' href={location.pageURL}>
              {location.title}
            </PopupLink>
            <Arrow target='_blank' href={location.pageURL}></Arrow>
          </MapPopupWrapper>
        </Popup>
      )}
    </>
  )
}

export const GymsMapLayers = (props: GymsMapLayerProps) => {
  const { mapData } = props
  const [statePins, setStatePins] = useState<defaultStateObject>(defaultStates)
  const [isStates, setIsStates] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  let statesList: string[] = props.states || []

  useEffect(() => {
    if (mapData) {
      statesList.map((state: string) => {
        let stateArray = null

        if (state === 'other') {
          stateArray = mapData.filter(
            md => md.state === undefined || md.state === ''
          )
        } else stateArray = mapData.filter(md => md.state === state)

        if (stateArray) {
          statePins[state] = stateArray
          setStatePins(statePins)
          setIsStates(true)
        }
      })

      setIsLoading(false)
    }
  }, [mapData])

  return !isLoading && isStates ? (
    <>
      {Object.entries(statePins).map(([stateKey, state]) => (
        <RenderLayers
          key={stateKey + '-pins'}
          {...props}
          mapData={state}
          state={stateKey}
        />
      ))}
    </>
  ) : !isLoading ? (
    <RenderLayers {...props} state='all' />
  ) : null
}

export default GymsMapLayers
